/** Utilitário compartilhado de upload de imagem: valida PNG/JPG e compacta arquivos grandes antes de enviar. */

export const TIPOS_IMAGEM_ACEITOS = "image/png,image/jpeg,image/jpg";
export const ACEITA_IMAGEM_PNG_JPG = ".png,.jpg,.jpeg";

const TAMANHO_MAXIMO_SEM_COMPACTAR = 2 * 1024 * 1024;
const MAIOR_LADO_MAXIMO_PX = 1200;
const QUALIDADE_JPEG = 0.88;

export function tipoImagemValido(arquivo: File): boolean {
  return /^image\/(png|jpe?g)$/i.test(arquivo.type);
}

function lerArquivoComoDataUrl(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    reader.readAsDataURL(arquivo);
  });
}

function carregarImagem(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Não foi possível processar a imagem."));
    img.src = src;
  });
}

/**
 * Lê o arquivo como data URL e, se estiver acima de ~2MB, compacta reduzindo o
 * maior lado para até 1200px (equivalente a uma imagem nítida em ~300dpi para
 * os tamanhos de exibição usados no sistema) antes de salvar no banco.
 */
export async function prepararImagemParaUpload(arquivo: File): Promise<string> {
  const dataUrlOriginal = await lerArquivoComoDataUrl(arquivo);
  if (arquivo.size <= TAMANHO_MAXIMO_SEM_COMPACTAR) return dataUrlOriginal;

  const img = await carregarImagem(dataUrlOriginal);
  const maiorLado = Math.max(img.width, img.height);
  const escala = maiorLado > MAIOR_LADO_MAXIMO_PX ? MAIOR_LADO_MAXIMO_PX / maiorLado : 1;
  const largura = Math.max(1, Math.round(img.width * escala));
  const altura = Math.max(1, Math.round(img.height * escala));

  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrlOriginal;
  ctx.drawImage(img, 0, 0, largura, altura);

  const manterPng = /^image\/png$/i.test(arquivo.type);
  return manterPng ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", QUALIDADE_JPEG);
}
